import { type Breakpoint } from '@builder4/shared/types/layout';
import { isFormSubNode } from '@builder4/shared/components/Form/formRegistry';

export const MAX_PADDING: Record<Breakpoint, number> = {
  desktop: 100,
  tablet: 20,
  mobile: 10,
};
export const CONTENT_BLOCK_NAME = 'Container';
export const BUTTON_NODE_NAME = 'Button';
export const COUNTDOWN_NODE_NAME = 'Countdown';
export const ICON_NODE_NAME = 'Icon';
export const TEXT_NODE_NAME = 'Text';
export const PAGE_NODE_NAME = 'Page';
export const IMAGE_NODE_NAME = 'Image';
export const VIDEO_NODE_NAME = 'Video';
export const EMBED_NODE_NAME = 'Embed';
export const FORM_NODE_NAME = 'Form';
export const AI_GENERATION_LOADER_NODE_NAME = 'AiGenerationLoader';
export const FORM_TEXT_FIELD_NODE_NAME = 'Form Text Field';
export const FORM_EMAIL_FIELD_NODE_NAME = 'Form Email Field';
export const FORM_PHONE_FIELD_NODE_NAME = 'Form Phone Field';
export const FORM_CONSENT_FIELD_NODE_NAME = 'Form Consent Field';
export const FORM_DROPDOWN_FIELD_NODE_NAME = 'Form Dropdown Field';
export const FORM_SELECTION_FIELD_NODE_NAME = 'Form Selection Field';
export const FORM_SUBMIT_BUTTON_NODE_NAME = 'Form Submit Button';
export const POPUP_NODE_NAME = 'Popup';

// Short field type labels for inspector Field Title defaults
export const FIELD_LABEL_TEXT = 'Text';
export const FIELD_LABEL_EMAIL = 'Email';
export const FIELD_LABEL_PHONE = 'Phone';
export const FIELD_LABEL_CONSENT = 'Consent';
export const FIELD_LABEL_DROPDOWN = 'Dropdown';
export const FIELD_LABEL_SELECTION = 'Selection';

export const CONTENT_BLOCK_HEIGHT = 512;
export const IMAGE_NODE_HEIGHT = 512;
export const VIDEO_NODE_HEIGHT = 315; // Default 16:9 aspect ratio

// Default heights for new elements dragged from sidebar, based on constants and actual render sizes
export const NEW_ELEMENT_DEFAULT_HEIGHTS: Record<string, number> = {
  [CONTENT_BLOCK_NAME]: CONTENT_BLOCK_HEIGHT,
  [IMAGE_NODE_NAME]: IMAGE_NODE_HEIGHT,
  [VIDEO_NODE_NAME]: VIDEO_NODE_HEIGHT,
  [FORM_NODE_NAME]: 180,
  [COUNTDOWN_NODE_NAME]: 80,
  [BUTTON_NODE_NAME]: 36,
  [TEXT_NODE_NAME]: 52,
  [ICON_NODE_NAME]: 40,
};

export const MAX_IMAGE_UPLOAD_HEIGHT = 500;
export const COPY_CRAFT_NODE_PREFIX = 'craft-node:';
export const PASTE_EVENT = 'paste' as const;
export const DUPLICATE_EVENT = 'duplicate' as const;

const nodeThemeColorMap: Record<string, string> = {
  [IMAGE_NODE_NAME]: 'green-600',
  [FORM_NODE_NAME]: 'form-theme',
  [CONTENT_BLOCK_NAME]: 'indigo-500',
  [ICON_NODE_NAME]: 'purple-500',
  [PAGE_NODE_NAME]: 'indigo-500',
  [TEXT_NODE_NAME]: 'teal-500',
  [VIDEO_NODE_NAME]: 'coral-500',
  [BUTTON_NODE_NAME]: 'orange-500',
  [POPUP_NODE_NAME]: 'indigo-500',
};

// Maps node names to their theme color classes
export const getNodeThemeColorClass = (nodeName: string) => {
  // Get color from map or default to indigo-500
  let color = nodeThemeColorMap[nodeName] ?? 'indigo-500';
  if (isFormSubNode(nodeName)) {
    color = 'form-theme';
  }
  return color;
};

// Outline color mapping to ensure Tailwind includes these classes
export const outlineColorClasses: Record<string, string> = {
  'green-600': 'outline-green-600',
  'indigo-500': 'outline-indigo-500',
  'purple-500': 'outline-purple-500',
  'teal-500': 'outline-teal-500',
  'coral-500': 'outline-coral-500',
  'orange-500': 'outline-orange-500',
  'lime-600': 'outline-lime-600',
  // Form custom outline color (#A8B910)
  'form-theme': 'outline-[#A8B910]',
};
// Background color mapping to ensure Tailwind includes these classes
export const bgColorClasses: Record<string, string> = {
  'green-600': '!bg-green-600',
  'indigo-500': '!bg-indigo-500',
  'purple-500': '!bg-purple-500',
  'teal-500': '!bg-teal-500',
  'coral-500': '!bg-coral-500',
  'orange-500': '!bg-orange-500',
  'lime-600': '!bg-lime-600',
  // Form controls background color should be solid (no opacity)
  'form-theme': '!bg-[#A8B910]',
};
